import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import { COLORS } from "../../common/colors";
import type { ComplianceDataset } from "src/providers/compliance/complianceTypes";
import {
  TRIAGE_ORDER,
  STREAM_DISPLAY
} from "src/providers/compliance/complianceTypes";
import type {
  MissingDataStream,
  SortField,
  SortDirection,
  TriageConfig
} from "src/providers/compliance/complianceFilters";
import { sortPatients } from "src/providers/compliance/complianceFilters";
import { COMPLIANCE_COLORS, FOCUSED_COLUMN_SX } from "./complianceColors";
import { COLUMN_WIDTHS, STREAM_MIN_WIDTH } from "./complianceLayout";
import { TriageGroup } from "./TriageGroup";

interface PatientListViewProps {
  dataset: ComplianceDataset;
  onPatientClick: (patientId: string) => void;
  // Highlighted while its sidebar is open.
  selectedPatientId: string | null;
  isFiltered?: boolean;
  visibleStreams: MissingDataStream[];
  focusedStream: MissingDataStream | null;
  sortField: SortField;
  sortDirection: SortDirection;
  onSortChange: (field: SortField) => void;
  // Cells use the same checks as triage.
  triageConfig: TriageConfig;
  now: Date; // when triage ran
}

const HEADER_SX = {
  fontSize: 13,
  fontWeight: 600,
  color: COLORS.GREY_800,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  lineHeight: 1.3
};

const handleKeyActivate =
  (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback();
    }
  };

const sortArrow = (
  field: SortField,
  currentField: SortField,
  direction: SortDirection
) => {
  const isActive = currentField === field;
  const Icon = isActive && direction === "asc" ? ArrowUpward : ArrowDownward;
  return (
    <Icon
      sx={{
        fontSize: 18,
        ml: 0.5,
        color: isActive ? COLORS.PRIMARY_1000 : COLORS.GREY_600
      }}
    />
  );
};

const ariaSort = (
  field: SortField,
  currentField: SortField,
  direction: SortDirection
): "ascending" | "descending" | "none" =>
  currentField !== field
    ? "none"
    : direction === "asc"
    ? "ascending"
    : "descending";

export function PatientListView({
  dataset,
  onPatientClick,
  selectedPatientId,
  isFiltered = false,
  visibleStreams,
  focusedStream,
  sortField,
  sortDirection,
  onSortChange,
  triageConfig,
  now
}: PatientListViewProps): JSX.Element {
  const patientsByTriage = useMemo(
    () =>
      TRIAGE_ORDER.map((triage) => {
        const group = dataset.patients.filter((p) => p.triage === triage);
        const sorted =
          sortField === "triage"
            ? group
            : sortPatients(group, sortField, sortDirection);
        return { triage, patients: sorted };
      }).filter(({ patients }) => patients.length > 0),
    [dataset.patients, sortField, sortDirection]
  );

  if (dataset.patients.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: COLORS.GREY_600 }}>
          {isFiltered
            ? "No patients match the current filters"
            : "No patients found"}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        role="table"
        aria-label="Patients"
        sx={{
          border: `1px solid ${COMPLIANCE_COLORS.BORDER_SECTION}`,
          borderRadius: "12px",
          backgroundColor: "#ffffff",
          // "clip" rounds the corners without creating a scroll container, which would break the sticky header
          overflow: "clip",
          minWidth: "min-content",
          mt: 1
        }}
      >
        {/* Column headers */}
        <Box
          role="row"
          sx={{
            display: "flex",
            alignItems: "stretch",
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "#ffffff",
            borderBottom: `2px solid ${COLORS.GREY_500}`,
            boxShadow: "0 2px 4px rgba(16, 24, 40, 0.12)"
          }}
        >
          <Typography
            role="columnheader"
            sx={{
              width: COLUMN_WIDTHS.patient,
              flexShrink: 0,
              px: "14px",
              py: "11px",
              ...HEADER_SX
            }}
          >
            Patient
          </Typography>
          <Typography
            role="columnheader"
            sx={{
              width: COLUMN_WIDTHS.appMode,
              flexShrink: 0,
              px: "12px",
              py: "11px",
              borderLeft: `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`,
              ...HEADER_SX
            }}
          >
            App mode
          </Typography>
          <Box
            role="columnheader"
            tabIndex={0}
            onClick={() => onSortChange("startDate")}
            onKeyDown={handleKeyActivate(() => onSortChange("startDate"))}
            aria-sort={ariaSort("startDate", sortField, sortDirection)}
            sx={{
              width: COLUMN_WIDTHS.startDate,
              flexShrink: 0,
              px: "12px",
              py: "11px",
              borderLeft: `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { backgroundColor: COMPLIANCE_COLORS.HOVER_BG }
            }}
          >
            <Typography sx={HEADER_SX}>Start date</Typography>
            {sortArrow("startDate", sortField, sortDirection)}
          </Box>
          <Box
            role="columnheader"
            tabIndex={0}
            onClick={() => onSortChange("createdAt")}
            onKeyDown={handleKeyActivate(() => onSortChange("createdAt"))}
            aria-sort={ariaSort("createdAt", sortField, sortDirection)}
            sx={{
              width: COLUMN_WIDTHS.createdAt,
              flexShrink: 0,
              px: "12px",
              py: "11px",
              borderLeft: `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { backgroundColor: COMPLIANCE_COLORS.HOVER_BG }
            }}
          >
            <Typography sx={HEADER_SX}>Created</Typography>
            {sortArrow("createdAt", sortField, sortDirection)}
          </Box>
          <Box
            role="columnheader"
            tabIndex={0}
            onClick={() => onSortChange("lastOutreach")}
            onKeyDown={handleKeyActivate(() => onSortChange("lastOutreach"))}
            aria-sort={ariaSort("lastOutreach", sortField, sortDirection)}
            sx={{
              width: COLUMN_WIDTHS.lastContact,
              flexShrink: 0,
              px: "12px",
              py: "11px",
              borderLeft: `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { backgroundColor: COMPLIANCE_COLORS.HOVER_BG }
            }}
          >
            <Typography sx={HEADER_SX}>Last contact</Typography>
            {sortArrow("lastOutreach", sortField, sortDirection)}
          </Box>
          {visibleStreams.includes("medication") && (
            <Typography
              role="columnheader"
              sx={{
                width: COLUMN_WIDTHS.medication,
                flexShrink: 0,
                px: "12px",
                py: "11px",
                borderLeft: `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`,
                ...HEADER_SX,
                ...(focusedStream === "medication" ? FOCUSED_COLUMN_SX : {})
              }}
            >
              Medication
            </Typography>
          )}
          {visibleStreams.some((s) => s !== "medication") && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                borderLeft: `1px solid ${COMPLIANCE_COLORS.BORDER_ROW}`
              }}
            >
              {visibleStreams
                .filter((s) => s !== "medication")
                .map((stream) => (
                  <Typography
                    key={stream}
                    role="columnheader"
                    sx={{
                      flex: 1,
                      minWidth: STREAM_MIN_WIDTH,
                      textAlign: "center",
                      py: "11px",
                      ...HEADER_SX,
                      ...(focusedStream === stream ? FOCUSED_COLUMN_SX : {})
                    }}
                  >
                    {STREAM_DISPLAY[stream]?.short ?? stream}
                  </Typography>
                ))}
            </Box>
          )}
        </Box>

        {/* Patient rows */}
        <Box>
          {patientsByTriage.map(({ triage, patients }) => (
            <TriageGroup
              key={triage}
              triage={triage}
              patients={patients}
              onPatientClick={onPatientClick}
              selectedPatientId={selectedPatientId}
              visibleStreams={visibleStreams}
              focusedStream={focusedStream}
              triageConfig={triageConfig}
              now={now}
            />
          ))}
        </Box>
      </Box>
    </>
  );
}
